import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { v3 } from '../lib/styleTokens';

const ACTION_COLORS = {
  CREATE: '#dcfce7', UPDATE: '#dbeafe', DELETE: '#fee2e2',
  ACTIVATE: '#dcfce7', DEACTIVATE: '#fef3c7',
  // v3.5.55: quote-lifecycle actions. Greens = positive/forward-progress,
  // blues = in-progress, ambers = needs-attention, reds = lost/refunded.
  SIGNATURE_REQUEST_SENT:    '#dbeafe',   // blue — in flight to client
  SIGNATURE_COMPLETED:       '#dcfce7',   // green — client signed
  SIGNATURE_DECLINED:        '#fee2e2',   // red — client rejected
  PAYMENT_LINK_EMAILED:      '#dbeafe',   // blue — auto-loop fired
  PAYMENT_LINK_EMAIL_FAILED: '#fee2e2',   // red — auto-loop broke
  PAYMENT_LINK_EXPIRED:      '#fef3c7',   // amber — client never paid
  PAYMENT_RECEIVED:          '#dcfce7',   // green — money in
  PAYMENT_FAILED:            '#fee2e2',   // red — card declined
  PAYMENT_REFUNDED:          '#fee2e2',   // red — money out
  HUBSPOT_FILE_ATTACHED:     '#e0e7ff',   // indigo — CRM hook
  HUBSPOT_CONNECT:           '#e0e7ff',
  HUBSPOT_SYNC:              '#e0e7ff',
  RATE_ACCEPTED:             '#dcfce7',   // green — pricing locked
  REVIEW_REQUESTED:          '#dbeafe',   // blue — peer review out
  REVIEW_APPROVED:           '#dcfce7',   // green — sign-off
  REVIEW_RETURNED:           '#fef3c7',   // amber — needs revision
};
const ACTION_TEXT = {
  CREATE: '#166534', UPDATE: '#1e40af', DELETE: '#991b1b',
  ACTIVATE: '#166534', DEACTIVATE: '#92400e',
  SIGNATURE_REQUEST_SENT:    '#1e40af',
  SIGNATURE_COMPLETED:       '#166534',
  SIGNATURE_DECLINED:        '#991b1b',
  PAYMENT_LINK_EMAILED:      '#1e40af',
  PAYMENT_LINK_EMAIL_FAILED: '#991b1b',
  PAYMENT_LINK_EXPIRED:      '#92400e',
  PAYMENT_RECEIVED:          '#166534',
  PAYMENT_FAILED:            '#991b1b',
  PAYMENT_REFUNDED:          '#991b1b',
  HUBSPOT_FILE_ATTACHED:     '#3730a3',
  HUBSPOT_CONNECT:           '#3730a3',
  HUBSPOT_SYNC:              '#3730a3',
  RATE_ACCEPTED:             '#166534',
  REVIEW_REQUESTED:          '#1e40af',
  REVIEW_APPROVED:           '#166534',
  REVIEW_RETURNED:           '#92400e',
};

const PAGE_SIZE = 50;
const FILTER_DEBOUNCE_MS = 300;

export default function ActivityPage() {
  const [logs,       setLogs]       = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(0);
  const [expanded,   setExpanded]   = useState(null);

  // Two layers of filter state:
  //   - filter:        the value used for the actual Supabase query
  //   - filterDraft:   what's in the input field right now (drives debounce)
  // For dropdowns (entity, action) the two are kept in sync immediately.
  // For the free-text user filter, filterDraft updates on every keystroke,
  // and `filter.user` only updates after FILTER_DEBOUNCE_MS of inactivity.
  const [filter,      setFilter]      = useState({ entity: '', user: '', action: '' });
  const [filterDraft, setFilterDraft] = useState({ entity: '', user: '', action: '' });

  // Debounce the user-email filter
  const userDebounceRef = useRef(null);
  useEffect(() => {
    if (userDebounceRef.current) clearTimeout(userDebounceRef.current);
    userDebounceRef.current = setTimeout(() => {
      if (filterDraft.user !== filter.user) {
        setFilter(f => ({ ...f, user: filterDraft.user }));
        setPage(0);
      }
    }, FILTER_DEBOUNCE_MS);
    return () => userDebounceRef.current && clearTimeout(userDebounceRef.current);
  }, [filterDraft.user]);

  async function load() {
    setLoading(true);
    let q = supabase.from('activity_log')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (filter.entity) q = q.eq('entity_type', filter.entity);
    if (filter.user)   q = q.ilike('user_email', `%${filter.user}%`);
    if (filter.action) q = q.eq('action', filter.action);
    const { data, count } = await q;
    setLogs(data || []);
    setTotalCount(count ?? 0);
    setLoading(false);
  }
  useEffect(() => { load(); }, [page, filter]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isLastPage = page >= totalPages - 1;

  function clearFilters() {
    setFilter({ entity: '', user: '', action: '' });
    setFilterDraft({ entity: '', user: '', action: '' });
    setPage(0);
  }

  function setDropdownFilter(key, value) {
    // Dropdown filters apply immediately (no debounce)
    setFilter(f => ({ ...f, [key]: value }));
    setFilterDraft(f => ({ ...f, [key]: value }));
    setPage(0);
  }

  const selStyle = { padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.label, background: v3.color.card, color: v3.color.body };
  const ctrlBtn = { padding: '8px 12px', background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.label, color: v3.color.body, cursor: 'pointer' };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: v3.color.bg, fontFamily: v3.font.family }}>
      {/* ── HEADER (fixed) ───────────────────────────────────────────── */}
      <div style={{ padding: '20px 20px 0', maxWidth: 1100, margin: '0 auto', width: '100%', boxSizing: 'border-box', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: v3.font.size.pageTitle, fontWeight: v3.font.weight.semibold, color: v3.color.body, margin: 0, letterSpacing: v3.font.letterSpacing.tight }}>Activity Log</h2>
            <p style={{ fontSize: v3.font.size.label, color: v3.color.muted, marginTop: 4, marginBottom: 0 }}>Quote lifecycle events (signatures, payments, reviews, rate acceptances) plus changes to products, packages, settings, and users</p>
          </div>
          <button onClick={load} style={ctrlBtn}>↺ Refresh</button>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {[['entity', 'Entity type', ['product','package','market_tier','setting','user','quote']],
            ['action', 'Action',      [
              // CRUD
              'CREATE','UPDATE','DELETE','ACTIVATE','DEACTIVATE',
              // v3.5.55: quote lifecycle
              'SIGNATURE_REQUEST_SENT','SIGNATURE_COMPLETED','SIGNATURE_DECLINED',
              'PAYMENT_LINK_EMAILED','PAYMENT_LINK_EMAIL_FAILED','PAYMENT_LINK_EXPIRED',
              'PAYMENT_RECEIVED','PAYMENT_FAILED','PAYMENT_REFUNDED',
              'HUBSPOT_CONNECT','HUBSPOT_SYNC','HUBSPOT_FILE_ATTACHED',
              'RATE_ACCEPTED',
              'REVIEW_REQUESTED','REVIEW_APPROVED','REVIEW_RETURNED',
            ]]].map(([key, placeholder, opts]) => (
            <select key={key} value={filterDraft[key]} onChange={e => setDropdownFilter(key, e.target.value)}
              style={selStyle}>
              <option value="">All {placeholder}s</option>
              {opts.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
          <input value={filterDraft.user}
            onChange={e => setFilterDraft(f => ({ ...f, user: e.target.value }))}
            placeholder="Filter by user email..."
            style={{ ...selStyle, minWidth: 200 }} />
          <button onClick={clearFilters} style={ctrlBtn}>Clear</button>
          {/* Subtle status indicator: shows when the displayed user filter is still being typed */}
          {filterDraft.user !== filter.user && (
            <span style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft, fontStyle: 'italic' }}>typing…</span>
          )}
        </div>
      </div>

      {/* ── TABLE (scrollable middle) ───────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0 20px', maxWidth: 1100, margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        {loading ? <div style={{ padding: 20, color: v3.color.muted, fontSize: v3.font.size.body }}>Loading...</div> : (
          logs.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: v3.color.mutedSoft, fontSize: v3.font.size.body }}>
              No activity matches the current filters.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: v3.font.size.body }}>
              <thead style={{ position: 'sticky', top: 0, background: v3.color.cardAlt, zIndex: 1 }}>
                <tr>
                  {['When','User','Action','Entity','Name','Changes'].map(h => (
                    <th key={h} style={{ padding: v3.space.cellPad, textAlign: 'left', color: v3.color.muted, fontWeight: v3.font.weight.semibold, borderBottom: `1px solid ${v3.color.border}`, fontSize: v3.font.size.micro, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase, background: v3.color.cardAlt }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((log, i) => (
                  <React.Fragment key={log.id}>
                    <tr style={{ borderBottom: `1px solid ${v3.color.borderSoft}`, background: i % 2 === 0 ? v3.color.card : v3.color.cardAlt, cursor: log.changes ? 'pointer' : 'default' }}
                      onClick={() => setExpanded(expanded === log.id ? null : log.id)}>
                      <td style={{ padding: '10px 16px', color: v3.color.muted, whiteSpace: 'nowrap', fontSize: v3.font.size.small, fontFamily: v3.font.mono }}>
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 16px', color: log.user_email ? v3.color.bodySoft : v3.color.muted, fontStyle: log.user_email ? 'normal' : 'italic' }}>
                        {log.user_email
                          ? log.user_email.split('@')[0]
                          : (log.metadata?.actor === 'system'
                              ? `system${log.metadata?.triggered_by ? ` (${log.metadata.triggered_by})` : ''}`
                              : '—')}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: v3.radius.pill, fontSize: v3.font.size.micro, fontWeight: v3.font.weight.bold, background: ACTION_COLORS[log.action] || v3.color.status.grayBg, color: ACTION_TEXT[log.action] || v3.color.bodySoft }}>
                          {log.action}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px', color: v3.color.muted }}>{log.entity_type}</td>
                      <td style={{ padding: '10px 16px', fontWeight: v3.font.weight.medium, color: v3.color.bodySoft }}>{log.entity_name}</td>
                      <td style={{ padding: '10px 16px', color: v3.color.muted, fontSize: v3.font.size.small }}>
                        {(() => {
                          // v3.5.55: rows are expandable if they have either `changes` (CRUD diff)
                          // or `metadata` with non-housekeeping keys (lifecycle events use metadata).
                          const hasChanges = log.changes && Object.keys(log.changes).length > 0;
                          const metaKeys = log.metadata
                            ? Object.keys(log.metadata).filter(k => k !== 'actor' && k !== 'triggered_by')
                            : [];
                          const hasMeta = metaKeys.length > 0;
                          if (!hasChanges && !hasMeta) return '—';
                          const total = (hasChanges ? Object.keys(log.changes).length : 0) + metaKeys.length;
                          return (
                            <span style={{ color: v3.color.ink, fontWeight: v3.font.weight.medium }}>
                              {expanded === log.id ? '▲ hide' : `▼ ${total} field${total !== 1 ? 's' : ''}`}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                    {expanded === log.id && (() => {
                      const hasChanges = log.changes && Object.keys(log.changes).length > 0;
                      const metaKeys = log.metadata
                        ? Object.keys(log.metadata).filter(k => k !== 'actor' && k !== 'triggered_by')
                        : [];
                      const hasMeta = metaKeys.length > 0;
                      if (!hasChanges && !hasMeta) return null;
                      return (
                        <tr style={{ background: '#f0f7ff' }}>
                          <td colSpan={6} style={{ padding: '8px 10px 10px 36px' }}>
                            {hasChanges && (
                              <>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Changes</div>
                                <table style={{ borderCollapse: 'collapse', fontSize: 11, marginBottom: hasMeta ? 12 : 0 }}>
                                  <thead><tr>{['Field','From','To'].map(h => <th key={h} style={{ textAlign: 'left', padding: '2px 12px 4px 0', color: '#6b7280', fontWeight: 600 }}>{h}</th>)}</tr></thead>
                                  <tbody>
                                    {Object.entries(log.changes).map(([field, change]) => (
                                      <tr key={field}>
                                        <td style={{ padding: '2px 12px 2px 0', fontWeight: 600, color: '#374151' }}>{field}</td>
                                        <td style={{ padding: '2px 12px 2px 0', color: '#dc2626', fontFamily: 'monospace' }}>{JSON.stringify(change.from ?? change)}</td>
                                        <td style={{ padding: '2px 0', color: '#166534', fontFamily: 'monospace' }}>{change.to !== undefined ? JSON.stringify(change.to) : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </>
                            )}
                            {hasMeta && (
                              <>
                                <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Details</div>
                                <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
                                  <tbody>
                                    {metaKeys.map(key => (
                                      <tr key={key}>
                                        <td style={{ padding: '2px 12px 2px 0', fontWeight: 600, color: '#374151', verticalAlign: 'top' }}>{key}</td>
                                        <td style={{ padding: '2px 0', color: '#1e40af', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                          {typeof log.metadata[key] === 'object' && log.metadata[key] !== null
                                            ? JSON.stringify(log.metadata[key])
                                            : String(log.metadata[key])}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>

      {/* ── FOOTER (pinned, pagination) ─────────────────────────────── */}
      <div style={{ flexShrink: 0, borderTop: `1px solid ${v3.color.border}`, background: v3.color.card, padding: '10px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ fontSize: v3.font.size.label, color: v3.color.muted }}>
            {totalCount > 0
              ? <>Page <strong style={{ color: v3.color.bodySoft }}>{page + 1}</strong> of <strong style={{ color: v3.color.bodySoft }}>{totalPages}</strong> · {totalCount.toLocaleString()} {totalCount === 1 ? 'entry' : 'entries'}</>
              : (loading ? 'Loading…' : 'No entries')}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              style={{ ...ctrlBtn, cursor: page === 0 ? 'not-allowed' : 'pointer', opacity: page === 0 ? 0.5 : 1 }}>← Prev</button>
            <button onClick={() => setPage(p => p + 1)} disabled={isLastPage}
              style={{ ...ctrlBtn, cursor: isLastPage ? 'not-allowed' : 'pointer', opacity: isLastPage ? 0.5 : 1 }}>Next →</button>
          </div>
        </div>
      </div>
    </div>
  );
}
